import { useEffect } from 'react';
import { ArrowDown } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import CircuitoHero from '../components/circuito/CircuitoHero';
import CircuitoLeadForm from '../components/circuito/CircuitoLeadForm';
import { CIRCUITO_LANDING } from '../content/circuitoLandingCopy';
import { CIRCUITO_IMG } from '../content/circuitoVisualAssets';
import { trackPublicEvent } from '../lib/publicAnalytics';

const BENEFICIOS = [
  {
    titulo: 'Valorização visível',
    texto: 'A política de cuidado deixa o discurso e aparece na unidade — um espaço concreto, no horário da jornada.',
  },
  {
    titulo: 'Proximidade',
    texto: 'O servidor não precisa se deslocar para um evento central. A experiência chega até o posto de trabalho.',
  },
  {
    titulo: 'Rotina respeitada',
    texto: 'Pausas, intervalos e trocas de turno. A unidade continua funcionando. O cuidado se encaixa no dia.',
  },
  {
    titulo: 'Capilaridade',
    texto: 'Hospital, escola, CRAS, sede. Um calendário percorre a rede — em vez de concentrar todos em um único endereço.',
  },
];

const SEMANA = [
  { dia: 'Seg', lugar: 'Hospital' },
  { dia: 'Ter', lugar: 'Unidade de saúde' },
  { dia: 'Qua', lugar: 'Escola' },
  { dia: 'Qui', lugar: 'CRAS' },
  { dia: 'Sex', lugar: 'Sede administrativa' },
];

const SECRETARIAS = [
  {
    id: 'saude',
    label: 'Saúde',
    img: CIRCUITO_IMG.servidora,
    headline: 'Quem cuida também precisa de um instante.',
  },
  {
    id: 'educacao',
    label: 'Educação',
    img: CIRCUITO_IMG.educacao,
    headline: 'Uma pausa entre as turmas, no próprio prédio.',
  },
  {
    id: 'assistencia',
    label: 'Assistência',
    img: CIRCUITO_IMG.assistencia,
    headline: 'Cuidar de quem acolhe — na unidade em que trabalha.',
  },
];

export default function CircuitoLanding() {
  const c = CIRCUITO_LANDING;

  useEffect(() => {
    document.title = c.pageTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', c.metaDescription);
    trackPublicEvent('circuito_view', { page: '/circuito' });
  }, [c.metaDescription, c.pageTitle]);

  return (
    <div className="flex min-h-screen flex-col bg-lunins-cream">
      <SiteHeader />

      <main className="flex-1">
        <CircuitoHero copy={c.hero} />

        {/* Benefícios para a organização */}
        <section id="beneficios" className="scroll-mt-24 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-6xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-lunins-gold">
              Para a Secretaria
            </p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium text-lunins-ink md:text-5xl">
              Uma política de cuidado que o servidor consegue usar.
            </h2>
            <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-lunins-border bg-lunins-surface sm:grid-cols-2">
              {BENEFICIOS.map((b) => (
                <article key={b.titulo} className="bg-lunins-cream p-8 md:p-10">
                  <h3 className="font-display text-lg font-semibold text-lunins-ink">{b.titulo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-lunins-muted">{b.texto}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Manifesto + foto de pessoa */}
        <section className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] lg:min-h-[640px]">
            <img
              src={CIRCUITO_IMG.servidora}
              alt="Servidora em um momento de pausa na unidade"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col justify-center bg-lunins-sand px-8 py-16 md:px-16 md:py-24">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-lunins-gold">Autocuidado</p>
            <blockquote className="mt-6 font-serif text-3xl font-medium leading-snug text-lunins-ink md:text-4xl">
              O autocuidado precisa caber na vida real.
            </blockquote>
            <p className="mt-8 max-w-md text-base leading-relaxed text-lunins-muted">
              Nem sempre dá para sair da unidade. Nem sempre há uma tarde inteira. Por isso a Lunins leva a
              experiência até o lugar do trabalho: um espaço temporário de pausa, no ritmo de cada equipe.
            </p>
            <p className="mt-6 text-sm text-lunins-muted">
              A instituição cria a condição. A pessoa encontra o momento.
            </p>
          </div>
        </section>

        {/* Circuito visual */}
        <section id="circuito" className="scroll-mt-24 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-lunins-gold">O circuito</p>
                <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium text-lunins-ink md:text-5xl">
                  Cada unidade recebe o seu dia.
                </h2>
              </div>
              <p className="max-w-sm text-sm leading-relaxed text-lunins-muted">
                A Secretaria escolhe a rede. A Lunins organiza o calendário, chega, prepara o ambiente e segue
                para o próximo ponto.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-2 gap-3 md:grid-cols-5">
              {SEMANA.map((d) => (
                <div
                  key={d.dia}
                  className="rounded-2xl border border-lunins-border bg-lunins-surface px-4 py-6 text-center"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-lunins-accent">{d.dia}</p>
                  <p className="mt-3 font-serif text-xl text-lunins-ink">{d.lugar}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-[11px] tracking-wide text-lunins-muted">
              Exemplo ilustrativo. O cronograma é desenhado com a sua rede.
            </p>
          </div>
        </section>

        {/* Chegada na unidade */}
        <section className="relative min-h-[70vh] overflow-hidden">
          <img
            src={CIRCUITO_IMG.montagem}
            alt="Equipe preparando um espaço de pausa dentro da unidade"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-lunins-night via-lunins-night/50 to-transparent" />
          <div className="relative mx-auto flex min-h-[70vh] max-w-6xl flex-col justify-end px-5 pb-16 md:px-8 md:pb-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-lunins-gold">Na unidade</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium text-lunins-cream md:text-5xl">
              Chegamos. Preparamos. Deixamos a pausa acontecer.
            </h2>
            <div className="mt-10 flex flex-wrap gap-8 text-sm text-lunins-cream/80">
              <span>Montagem</span>
              <span className="text-lunins-gold">·</span>
              <span>Ambiente disponível</span>
              <span className="text-lunins-gold">·</span>
              <span>Desmontagem</span>
              <span className="text-lunins-gold">·</span>
              <span>Próxima unidade</span>
            </div>
          </div>
        </section>

        {/* Escala */}
        <section className="px-5 py-20 md:px-8 md:py-24">
          <div className="mx-auto max-w-6xl">
            <h2 className="max-w-2xl font-serif text-3xl font-medium text-lunins-ink md:text-4xl">
              A rede pode ser percorrida em mais de uma frente.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {[
                { n: '1', t: 'Essencial', d: 'Uma unidade por vez. Semana a semana.' },
                { n: '2', t: 'Ampliado', d: 'Duas unidades no mesmo dia, quando o projeto pede.' },
                { n: '3', t: 'Expandido', d: 'Até três frentes simultâneas, conforme contratação.' },
              ].map((f) => (
                <article key={f.n} className="border-t border-lunins-gold/40 pt-6">
                  <p className="font-serif text-5xl text-lunins-gold">{f.n}</p>
                  <h3 className="mt-4 font-display text-lg font-semibold text-lunins-ink">{f.t}</h3>
                  <p className="mt-2 text-sm text-lunins-muted">{f.d}</p>
                </article>
              ))}
            </div>
            <p className="mt-8 text-xs text-lunins-muted">
              Depende de equipe, distância, público e o que a Secretaria contratar. Não é capacidade ilimitada.
            </p>
          </div>
        </section>

        {/* Secretarias */}
        <section id="areas" className="scroll-mt-24 px-5 pb-8 md:px-8">
          <div className="mx-auto max-w-6xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-lunins-gold">Redes</p>
            <h2 className="mt-4 font-serif text-3xl font-medium text-lunins-ink md:text-4xl">Saúde. Educação. Assistência.</h2>
            <div className="mt-12 grid gap-4 md:grid-cols-3">
              {SECRETARIAS.map((s) => (
                <article key={s.id} className="group relative overflow-hidden rounded-3xl">
                  <div className="aspect-[3/4]">
                    <img
                      src={s.img}
                      alt={s.headline}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-lunins-night via-lunins-night/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-lunins-gold">{s.label}</p>
                    <h3 className="mt-2 font-serif text-2xl text-lunins-cream">{s.headline}</h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Setembro Amarelo */}
        <section id="setembro" className="relative mt-16 min-h-[56vh] overflow-hidden">
          <img
            src={CIRCUITO_IMG.rede}
            alt="A cidade à noite — o cuidado percorrendo diferentes unidades"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-lunins-cream/70" />
          <div className="relative mx-auto flex min-h-[56vh] max-w-6xl flex-col justify-center px-5 py-20 md:px-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-lunins-gold">Setembro Amarelo</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium text-lunins-ink md:text-5xl">
              Uma campanha que percorre a rede — não um único palco.
            </h2>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-lunins-ink/80">
              Conscientização, valorização e espaço para o autocuidado, unidade a unidade. Sem associar a pausa a
              resultado clínico.
            </p>
          </div>
        </section>

        {/* Form */}
        <section id="formulario" className="scroll-mt-24 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-6xl gap-16 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <h2 className="font-serif text-3xl font-medium text-lunins-ink md:text-4xl">
                Vamos desenhar o circuito da sua rede?
              </h2>
              <p className="mt-4 max-w-lg text-sm text-lunins-muted">
                Conte a quantidade de unidades e o período. A Lunins devolve um esboço de calendário.
              </p>
              <div className="mt-10">
                <CircuitoLeadForm />
              </div>
            </div>
            <div id="faq" className="lg:col-span-2">
              <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-lunins-muted">
                Em poucas palavras
              </h3>
              <div className="mt-6 space-y-2">
                {c.faq.items.slice(0, 4).map((item) => (
                  <details
                    key={item.pergunta}
                    className="group border-b border-lunins-border py-4"
                  >
                    <summary className="cursor-pointer list-none text-sm font-medium text-lunins-ink [&::-webkit-details-marker]:hidden">
                      <span className="flex items-center justify-between gap-3">
                        {item.pergunta}
                        <ArrowDown className="h-3.5 w-3.5 shrink-0 text-lunins-gold transition group-open:rotate-180" />
                      </span>
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-lunins-muted">{item.resposta}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
